/* eslint-disable @typescript-eslint/no-explicit-any */
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Accordion from '@mui/material/Accordion'
import AccordionDetails from '@mui/material/AccordionDetails'
import AccordionSummary from '@mui/material/AccordionSummary'
import Typography from '@mui/material/Typography'
import { useConfirm } from 'material-ui-confirm'
import { useCallback, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import * as Yup from 'yup'

import { createEventLog, updateParticipant } from 'api/mutations'
import { FORM_ORDER_OMIT_FIELDS } from 'constants/api'
import { formatDate } from 'constants/formats'
import { EVENT_LOG_ACTION_TYPES } from 'constants/models'
import { Category, Event, LoggedInUser, Participant, Toast } from 'models/API'
import { doMutation } from 'utils/ApiUtils'

import Details from './Details'
import Form from './form'
import schema from './form/schema'

interface Props {
  data: Participant
  event: Event
  index: number
  loggedInUser: LoggedInUser
  toast: (toast: Toast) => void
}

const PARTICIPANT_FIELDS = [
  'bibName',
  'bibNumber',
  'firstName',
  'lastName',
  'emergencyContactName',
  'emergencyContactPhone',
  'teamName',
  'categoryId',
  'subCategory',
  'shirtSize',
  'shirtType',
  'shirtSize2',
  'shirtType2',
  'jerseySize',
  'birthdate',
  'gender',
  'nationality',
  'email',
  'phoneNumber',
  'address',
  'tandemName',
  'tandemBirthdate',
  'tandemShirtSize',
  'additionalId',
  'firstParticipantInfo',
  'relayEvent',
  'mandatoryCheck',
]

interface Diff {
  oldData: any
  newData: any
}

const DATE_FORMAT = 'YYYY-MM-DD'
const getDifference = (objA: any, objB: any) => {
  const diff: Diff = { oldData: {}, newData: {} }

  // Find keys in A that are different in B or missing in B
  Object.keys(objA)
    .filter(
      (key) =>
        PARTICIPANT_FIELDS.includes(key) &&
        !FORM_ORDER_OMIT_FIELDS.includes(key) &&
        !['tandemName', 'tandemBirthdate', 'tandemShirtSize'].includes(key),
    )
    .forEach((key) => {
      if (['birthdate', 'tandemBirthdate'].includes(key)) {
        const dateA =
          typeof objA[key] === 'string'
            ? objA[key]
            : formatDate(objA[key], DATE_FORMAT)
        const dateB =
          typeof objB[key] === 'string'
            ? objB[key]
            : formatDate(objB[key], DATE_FORMAT)
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          dateA !== dateB
        ) {
          diff.oldData[key] = dateA
          diff.newData[key] = dateB
        }
      } else if (['relayEvent'].includes(key)) {
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          (objA[key] ?? '') !== objB[key].join(',')
        ) {
          diff.oldData[key] = objA[key]
          diff.newData[key] =
            objB[key].join(',').length > 0 ? objB[key].join(',') : null
        }
      } else {
        if (
          !Object.prototype.hasOwnProperty.call(objB, key) ||
          (objA[key] ?? '') !== (objB[key] ?? '')
        ) {
          diff.oldData[key] = objA[key]
          diff.newData[key] = objB[key]
        }
      }
    })

  // Find keys in B that are missing in A (the 'd: 5' case)
  Object.keys(objB)
    .filter(
      (key) =>
        PARTICIPANT_FIELDS.includes(key) &&
        !FORM_ORDER_OMIT_FIELDS.includes(key) &&
        !['tandemName', 'tandemBirthdate', 'tandemShirtSize'].includes(key),
    )
    .forEach((key) => {
      if (!Object.prototype.hasOwnProperty.call(objA, key)) {
        diff.oldData[key] = objA[key]
        diff.newData[key] = objB[key]
      }
    })

  return diff
}

const getCategoryName = (categoryId: string, categories: Category[]) => {
  const category = categories.find((cat) => cat.id === categoryId)
  return category ? category.name : 'Unknown Category'
}

const Changes = ({
  diff,
  categories,
}: {
  diff: Diff
  categories: Category[]
}) => {
  const keys = Object.keys(diff.newData)

  if (keys.length === 0) {
    return <Typography>No changes detected</Typography>
  }

  return (
    <div>
      {keys.map((key) => {
        const oldValue =
          key === 'categoryId'
            ? getCategoryName(diff.oldData[key], categories)
            : diff.oldData[key] ?? <i>[NONE]</i>
        const newValue =
          key === 'categoryId'
            ? getCategoryName(diff.newData[key], categories)
            : diff.newData[key] ?? <i>[NONE]</i>

        return (
          <div key={key} style={{ marginBottom: '8px' }}>
            <Typography variant="body2">
              <strong>{key === 'categoryId' ? 'category' : key}:</strong>
            </Typography>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Typography
                variant="body2"
                style={{
                  flex: 1,
                  textDecoration: 'line-through',
                  color: 'red',
                  wordBreak: 'break-word',
                }}>
                {oldValue}
              </Typography>
              <span style={{ flexShrink: 0 }}>→</span>
              <Typography
                variant="body2"
                style={{
                  flex: 1,
                  color: 'green',
                  wordBreak: 'break-word',
                }}>
                {newValue}
              </Typography>
            </div>
          </div>
        )
      })}
    </div>
  )
}

const Item: React.FC<Props> = ({
  data,
  event,
  index,
  loggedInUser,
  toast,
}: Props) => {
  const confirm = useConfirm()
  const [participant, setParticipant] = useState(data)
  const [edit, setEdit] = useState(false)

  const handleEditClick = useCallback(() => {
    setEdit(!edit)
  }, [edit])

  const handleSubmit = useCallback(
    async (values: Yup.InferType<typeof schema>) => {
      const diff = getDifference(participant, values)

      const { confirmed } = await confirm({
        title: 'Changes to participant details',
        description: (
          <Changes
            diff={diff}
            categories={event?.categories?.items as unknown as Category[]}
          />
        ),
        confirmationText: 'Confirm',
        confirmationButtonProps: {
          variant: 'outlined',
          color: 'primary',
        },
        cancellationButtonProps: {
          variant: 'text',
          color: 'secondary',
        },
      })

      if (confirmed) {
        const eventLog = {
          id: uuidv4(),
          eventId: event.id,
          staffId: loggedInUser.sub,
          action: EVENT_LOG_ACTION_TYPES.UPDATE,
          description: `Updated participant details for ${participant.firstName} ${participant.lastName}`,
          newData: JSON.stringify(diff.newData),
          oldData: JSON.stringify({ participant, oldData: diff.oldData }),
        }

        try {
          await doMutation('updateParticipant', updateParticipant, {
            input: {
              id: participant.id,
              ...diff.newData,
            },
          })

          await doMutation('createEventLog', createEventLog, {
            input: eventLog,
          })
          setParticipant({
            ...participant,
            ...diff.newData,
          })
          setEdit(false)
          toast({
            title: 'Success',
            message: 'Participant details updated successfully',
          })
        } catch (e) {
          console.log(e)
        }
      }

      // console.log(reason)
      //=> "confirm" | "cancel" | "natural" | "unmount"
    },
    [participant],
  )

  return (
    <Accordion>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`panel${index}-content`}
        id={`panel${index}-header`}>
        <Typography component="span">
          {participant.firstName} {participant.lastName}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        {edit ? (
          <Form
            onSubmit={handleSubmit}
            onCancel={handleEditClick}
            participant={participant}
            event={event}
          />
        ) : (
          <Details
            participant={participant}
            event={event}
            onClick={handleEditClick}
          />
        )}
      </AccordionDetails>
    </Accordion>
  )
}

export default Item
